import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {Pagination} from 'lib/Listings';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {Cell, TABLE_PADDING} from 'notebook/tables/TableConstants';
import {FillPlateModalReducerActionType} from 'samples/plate-maps/fill-plate-modal/machine/FillPlateModalConstants';
import {FillPlateModalReducerDispatch} from 'samples/plate-maps/fill-plate-modal/useFillPlateModalReducer';
import {
  PlateMap,
  PlateMaterialInput,
  PlateModalAnalyticsMetadata,
  ReagentAssignmentGroup,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {BaseDetailStructuredTableRowMetadata} from 'samples/plate-maps/transfer-table/BaseDetailTransferStructuredTableAdapter';
import {
  CELL_FIELDS_TO_PICK,
  FillPlateTransferTableViewMode,
  ReagentMeasurementCellsByWells,
  SharedTransferTableColumnKey,
  DetailViewTransferTableColumnKey,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {getWellViewIndicesByColumnKey} from 'samples/plate-maps/transfer-table/FillPlateTransferTableUtil';
import WellViewStructuredTableAdapter, {
  WellViewStructuredTable,
} from 'samples/plate-maps/transfer-table/WellViewStructuredTableAdapter';
import {getWellViewRowsContents} from 'samples/plate-maps/transfer-table/WellViewTransferTableUtil';
import {GRAY, GRAY_LIGHTER} from 'ui-library/Colors';
import ResultsPager from 'ui-library/ResultsPager';
import pick from 'util/pick';

export interface WellViewStructuredTableProps {
  /** A function to dispatch an update to the transfer table state. */
  dispatch: FillPlateModalReducerDispatch;
  /** Information about how to page through the selected wells. */
  pagination: Pagination;
  /** The plate map being filled. */
  plateMap: PlateMap;
  /** In a procedure context, a list of plate-used material inputs. */
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /** The set of reagent schemas in the plate. */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected */
  selectedWellIndices: Set<number>;
  /** Analytics metadata included with any analytics events */
  analyticsMetadata: PlateModalAnalyticsMetadata;
  testOrderInfoById: TestOrderInfoById;
}

/**
 * The Well View of the new transfer table. Every content and source of every well is displayed in this view.
 * Rows are grouped by well, and then by co-occurrence in a container. A row in this table corresponds
 * directly with a well content or source.
 */
export default function WellViewTransferTable({
  dispatch,
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  analyticsMetadata,
  testOrderInfoById,
}: WellViewStructuredTableProps): JSX.Element {
  const cid = 'fill-plate-well-view-structured-table';
  const adapter = WellViewStructuredTableAdapter.getAdapter();

  const showMaterialInputColumn = plateMaterialInputs != null;
  const indicesByColumnKey = getWellViewIndicesByColumnKey(showMaterialInputColumn);
  const rowsContentsByColumnKey = getWellViewRowsContents({
    pagination,
    plateMap,
    plateMaterialInputs,
    reagentGroupsById,
    selectedWellIndices,
    showMaterialInputColumn,
  });
  const tableMetadata = {
    plateMaterialInputs: plateMaterialInputs ?? [],
    reagentGroupsById,
    showMaterialInputColumn,
    testOrderInfoById,
  };

  if (rowsContentsByColumnKey.length === 0) {
    return <div />;
  }

  function buildStructuredTableRows(): Array<StructuredTableRow<BaseDetailStructuredTableRowMetadata>> {
    const rows: Array<StructuredTableRow<BaseDetailStructuredTableRowMetadata>> = [];
    for (const [index, rowContents] of rowsContentsByColumnKey.entries()) {
      const row = produce(
        adapter.getBlankRow(
          tableMetadata,
          index,
          adapter.getAllSupportedColumns({
            entryId: '',
            metadata: tableMetadata,
            schemas: [],
            selectorsById: {},
            structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
          })
        ),
        (draft) => {
          draft.metadata.schemaId = rowContents.schemaId;
          draft.metadata.wellIndex = rowContents.wellIndex;
          draft.metadata.isContent = rowContents.isExistingContent;
          draft.metadata.isEmpty = rowContents.isEmpty;
          draft.metadata.containerOrContainableId = rowContents.containerOrContainableId;
          draft.metadata.containableId = rowContents.containableId;
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.WELL]] =
            rowContents[DetailViewTransferTableColumnKey.WELL];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_ICON]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_ICON];
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.SOURCE]] =
            rowContents[DetailViewTransferTableColumnKey.SOURCE];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_CONCENTRATION]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_CONCENTRATION];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT]] =
            rowContents[SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.TRANSFER_QUANTITY]] =
            rowContents[SharedTransferTableColumnKey.TRANSFER_QUANTITY];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT]] =
            rowContents[SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.FINAL_CONCENTRATION]] =
            rowContents[SharedTransferTableColumnKey.FINAL_CONCENTRATION];
          draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT]] =
            rowContents[SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT];
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.FINAL_QUANTITY]] =
            rowContents[DetailViewTransferTableColumnKey.FINAL_QUANTITY];
          draft.cells[indicesByColumnKey[DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT]] =
            rowContents[DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT];
          if (rowContents.hasOwnProperty(SharedTransferTableColumnKey.MATERIAL_INPUT)) {
            draft.cells[indicesByColumnKey[SharedTransferTableColumnKey.MATERIAL_INPUT]] =
              rowContents[SharedTransferTableColumnKey.MATERIAL_INPUT]!;
          }
        }
      );
      rows.push(row);
    }
    return rows;
  }

  function pickWellByIndex(
    row: StructuredTableRow<BaseDetailStructuredTableRowMetadata>,
    key: SharedTransferTableColumnKey | DetailViewTransferTableColumnKey
  ): Cell {
    return pick(row.cells[indicesByColumnKey[key]], ...CELL_FIELDS_TO_PICK);
  }

  function onUpdate(structuredTable: WellViewStructuredTable): void {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.EDITED_TRANSFER_TABLE, {
      ...analyticsMetadata,
      viewMode: FillPlateTransferTableViewMode.WELL,
    });
    const reagentMeasurementCellsByWells: ReagentMeasurementCellsByWells = {};
    for (const row of structuredTable.rows) {
      const {wellIndex, isContent, isEmpty, containerOrContainableId, containableId} = row.metadata;
      reagentMeasurementCellsByWells[wellIndex] ??= {
        contentCells: {},
        sourceCells: {},
        finalQuantity: pickWellByIndex(row, DetailViewTransferTableColumnKey.FINAL_QUANTITY),
        finalQuantityUnit: pickWellByIndex(row, DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT),
      };

      if (isEmpty) {
        continue;
      }

      if (isContent) {
        reagentMeasurementCellsByWells[wellIndex].contentCells[containerOrContainableId] = {
          finalConcentration: pickWellByIndex(row, SharedTransferTableColumnKey.FINAL_CONCENTRATION),
          finalConcentrationUnit: pickWellByIndex(row, SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT),
        };
        if (showMaterialInputColumn) {
          reagentMeasurementCellsByWells[wellIndex].contentCells[containerOrContainableId].materialInput =
            pickWellByIndex(row, SharedTransferTableColumnKey.MATERIAL_INPUT);
        }
      } else {
        reagentMeasurementCellsByWells[wellIndex].sourceCells[containerOrContainableId] ??= {
          transferQuantity: pickWellByIndex(row, SharedTransferTableColumnKey.TRANSFER_QUANTITY),
          transferQuantityUnit: pickWellByIndex(row, SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT),
          contents: {},
        };
        reagentMeasurementCellsByWells[wellIndex].sourceCells[containerOrContainableId].contents[
          containableId ?? containerOrContainableId
        ] = {
          finalConcentration: pickWellByIndex(row, SharedTransferTableColumnKey.FINAL_CONCENTRATION),
          finalConcentrationUnit: pickWellByIndex(row, SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT),
          sourceConcentration: pickWellByIndex(row, SharedTransferTableColumnKey.SOURCE_CONCENTRATION),
          sourceConcentrationUnit: pickWellByIndex(
            row,
            SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT
          ),
        };
        if (showMaterialInputColumn) {
          reagentMeasurementCellsByWells[wellIndex].sourceCells[containerOrContainableId].contents[
            containableId ?? containerOrContainableId
          ].materialInput = pickWellByIndex(row, SharedTransferTableColumnKey.MATERIAL_INPUT);
        }
      }
      // TODO(BNCH-69096): Handle auto updating final concentration when diluting empty wells
    }
    dispatch({
      type: FillPlateModalReducerActionType.UPDATE_REAGENT_MEASUREMENTS_BY_WELLS,
      reagentMeasurementCellsByWells,
    });
  }

  const table = adapter.createTable({
    name: '',
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  table.rows = buildStructuredTableRows();

  return (
    <>
      <div className={css(styles.tableWrapper)}>
        <StructuredTableOutsideNotebook
          canRename={false}
          cid={cid}
          hideColumnHeaderIcons={true}
          includeUnsubmittedStatus={false}
          isCollapsible={false}
          structuredTable={{...table, cid}}
          suppressTitleBar={true}
          hideFooter={true}
          updateHandler={onUpdate}
          enableFormulas={false}
          preserveMergedCellsInPaste={true}
        />
      </div>
      <div className={css(styles.paginationWrapper)}>
        <ResultsPager
          allowFastForwardAndBackward={true}
          isLoading={false}
          setOffset={(offset: number) => {
            dispatch({type: FillPlateModalReducerActionType.UPDATE_PAGINATION_OFFSET, offset});
          }}
          pagination={pagination}
          resultDescription="well"
        />
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  paginationWrapper: {
    backgroundColor: GRAY_LIGHTER,
    border: `1px solid ${GRAY}`,
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    // Overlap the internal padding on the TableEditable
    marginTop: '-11px',
    marginRight: TABLE_PADDING,
    padding: '12px',
  },
  tableWrapper: {
    marginLeft: -TABLE_PADDING,
  },
});
